<script setup lang="ts">
import { PhFolder } from '@phosphor-icons/vue';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ModalTransition } from '../modal-transition';
import { useFileManagerDelete } from '../../logic/core/useFileManagerDelete';
import type { FileSystemItem } from '../../types';
import { describeItems } from '../../utils';
import { getFileIcon } from '../../views/file-explorer-view/sub-components/render-modes/_shared/file-object/sub-components/file-item/get-file-icon';

const props = defineProps<{
  target: FileSystemItem[];
}>();

const { t } = useI18n();

// Delete-specific rather than a generic reusable ConfirmModal (a deliberate choice — see
// DELETE_ITEM_PLAN.md): the engineering investment here goes into the logic layer
// (useFileManagerDelete's aggregation), not into a premature UI abstraction for a pattern that has
// exactly one user so far.
//
// Self-injects useFileManagerDelete() rather than emitting 'confirm'/'close' up to FileManager.vue
// (unlike NewFolderModal, which does emit): isConfirmOpen/pendingTarget/confirmDelete/cancelDelete
// already live in that composable's provided state, so calling confirmDelete()/cancelDelete()
// directly here avoids threading props/emits through FileManager.vue for no reason. Closer in
// spirit to PreviewModal, which also derives its content directly from injected state rather than
// from props.
const { confirmDelete, cancelDelete } = useFileManagerDelete();

const breakdown = computed(() => describeItems(props.target));

// Single item: name it directly ("report.pdf" / "Contracts"). Multi-item: describe the mix in
// plain language rather than trying to cram file/folder counts into a single generic noun.
const title = computed(() => {
  const { total, fileCount, directoryCount } = breakdown.value;
  if (total === 1)
    return t('fileManager.deleteConfirmModal.titleSingle', {
      name: props.target[0].name,
    });
  if (fileCount > 0 && directoryCount > 0) {
    return t('fileManager.deleteConfirmModal.titleMixed', {
      fileCount,
      directoryCount,
    });
  }
  if (directoryCount > 0)
    return t('fileManager.deleteConfirmModal.titleFoldersOnly', {
      directoryCount,
    });
  return t('fileManager.deleteConfirmModal.titleFilesOnly', { fileCount });
});

// Only mentions folder recursion when a folder is actually part of the target — a files-only
// delete has no subtree to warn about. Built as one complete sentence here (rather than stitched
// together with a conditional template fragment) so the punctuation is always correct regardless of
// which branch renders. Deliberately storage-agnostic wording — this file-manager is a UI over
// SharePoint today, but the warning shouldn't name that backend.
const warningText = computed(() => {
  const recursionClause =
    breakdown.value.directoryCount > 0
      ? t('fileManager.deleteConfirmModal.recursionWarning')
      : '';
  return t('fileManager.deleteConfirmModal.warningBase') + recursionClause;
});

// Per-item icon for the list below — same folder/file distinction DetailsView.vue's own `icon`
// computed already draws (PhFolder for directories, getFileIcon(name) for files), reused here so a
// folder and a file read as visually distinct rows rather than a plain bulleted name list.
const itemIcon = (item: FileSystemItem) => {
  if (item.type === 'directory') {
    return { component: PhFolder, color: 'var(--primary-color)' };
  }
  const fileIcon = getFileIcon(item.name);
  return { component: fileIcon.icon, color: fileIcon.color };
};

// A deliberate friction delay on the Delete button itself — separate from the confirm modal
// existing in the first place — so a reflexive/muscle-memory click right after the modal opens
// can't complete a destructive action before the user has actually registered what's listed above.
// Starts fresh every time this component mounts (i.e. every time the modal opens), not once
// globally, since a user re-opening the modal on a different selection should get the same delay
// again rather than an already-expired one.
const COUNTDOWN_SECONDS = 1;
const secondsRemaining = ref(COUNTDOWN_SECONDS);
let countdownInterval: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
  countdownInterval = setInterval(() => {
    secondsRemaining.value -= 1;
    if (secondsRemaining.value <= 0) {
      clearInterval(countdownInterval);
      countdownInterval = undefined;
    }
  }, 1000);
});

onBeforeUnmount(() => {
  if (countdownInterval) clearInterval(countdownInterval);
});

const isDeleteDisabled = computed(() => secondsRemaining.value > 0);
const deleteButtonLabel = computed(() =>
  secondsRemaining.value > 0
    ? t('fileManager.deleteConfirmModal.deleteWithCountdown', {
        seconds: secondsRemaining.value,
      })
    : t('fileManager.deleteConfirmModal.delete'),
);
</script>

<template>
  <ModalTransition>
    <div class="modal is-active" role="dialog" aria-modal="true">
      <div class="modal-background" @click="cancelDelete"></div>

      <div class="modal-card delete-confirm-modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ title }}</p>
          <button
            class="delete"
            :aria-label="t('fileManager.deleteConfirmModal.closeAriaLabel')"
            type="button"
            @click="cancelDelete"
          ></button>
        </header>

        <section class="modal-card-body">
          <p class="warning-text">{{ warningText }}</p>
          <ul class="delete-target-list">
            <li v-for="item in target" :key="item.path" class="delete-target-row">
              <component
                :is="itemIcon(item).component"
                :size="16"
                :color="itemIcon(item).color"
              />
              <span class="delete-target-name" :title="item.name">{{
                item.name
              }}</span>
            </li>
          </ul>
        </section>

        <footer class="modal-card-foot actions">
          <button type="button" class="button is-light" @click="cancelDelete">
            {{ t('fileManager.deleteConfirmModal.cancel') }}
          </button>
          <button
            type="button"
            class="button is-danger confirm-delete-button"
            :disabled="isDeleteDisabled"
            @click="confirmDelete"
          >
            {{ deleteButtonLabel }}
          </button>
        </footer>
      </div>
    </div>
  </ModalTransition>
</template>

<style scoped lang="scss">
.delete-confirm-modal-card {
  width: min(460px, 92vw);

  .modal-card-head,
  .modal-card-foot {
    padding: 0.75rem 1.5rem;
  }

  .modal-card-title {
    font-size: 1.1rem;
  }

  .delete {
    height: 24px;
    width: 24px;
    max-height: 24px;
    max-width: 24px;
  }
}

.actions {
  justify-content: flex-end;
  gap: 1rem;
}

// bulma's .is-danger already sets white text, but this pins it explicitly (including the disabled
// countdown state, which would otherwise dim toward its default disabled-button gray) so the label
// stays legibly white for the button's entire lifecycle, countdown included.
.confirm-delete-button,
.confirm-delete-button:disabled {
  color: var(--white);
}

// A little darker than bulma's default .is-danger red — same color-mix-toward-black approach
// DetailsView.vue's own hover states already use (e.g. .open-sharepoint-button:hover), just applied
// at rest here rather than only on hover.
.confirm-delete-button {
  background-color: color-mix(in srgb, var(--text-danger) 85%, black);
  border-color: color-mix(in srgb, var(--text-danger) 85%, black);
}

.confirm-delete-button:hover:not(:disabled) {
  background-color: color-mix(in srgb, var(--text-danger) 70%, black);
  border-color: color-mix(in srgb, var(--text-danger) 70%, black);
}

/* Capped height + scroll rather than letting the modal grow unbounded — a large multi-selection
   should still fit on screen, not push the Cancel/Delete footer off it. */
.delete-target-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  max-height: 220px;
  padding: 0.5rem;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0.5rem;
}

.delete-target-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.delete-target-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.warning-text {
  margin-bottom: 0.75rem;
  color: #6b7280;
  font-size: 0.875rem;
}
</style>
